import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useLocation, useParams } from 'react-router-dom';
import {
  useCreateStudentActivityMutation,
  useUpdateStudentActivityMutation,
} from 'src/pages/single-student/singleStudentApi';
import { useAppDispatch } from 'src/store';
import { addToast } from 'src/toasts/ToastsSlice';

import { useInvoiceDetailsQuery } from '../billing/billingApi';
import { ActivityFormData, activitySchema } from './ActivityForm.schema';

type UseActivityFormProps = {
  onOpenChange: (open: boolean) => void;
  activity?: ActivityFormData & { id: string };
  month?: number;
  year?: number;
  preselectedActivity?: string;
};

const defaultValues = (preselectedActivity?: string): ActivityFormData => ({
  name: preselectedActivity || '',
  date: '',
  durationInMinutes: 10,
  note: '',
});

export const useActivityForm = ({
  onOpenChange,
  activity,
  preselectedActivity,
}: UseActivityFormProps) => {
  const { id } = useParams();
  const location = useLocation();
  const isStudentProfile = location.pathname.includes('student');
  const dispatch = useAppDispatch();
  const [createActivity, { isLoading: isCreating }] = useCreateStudentActivityMutation();
  const [updateActivity, { isLoading: isUpdating }] = useUpdateStudentActivityMutation();
  const { data: invoiceData } = useInvoiceDetailsQuery(id!, {
    skip: !id || isStudentProfile,
  });
  const studentId = isStudentProfile ? id! : invoiceData?.studentId || '';

  const methods = useForm<ActivityFormData>({
    resolver: zodResolver(activitySchema),
    defaultValues: activity || defaultValues(preselectedActivity),
  });

  const handleSave = methods.handleSubmit(async (data) => {
    try {
      if (activity?.id) {
        await updateActivity({
          id: studentId,
          activityId: activity.id,
          activity: data,
        });
        dispatch(addToast({ text: 'Activity updated successfully', type: 'positive' }));
        methods.reset(data, { keepDirty: false, keepErrors: false, keepTouched: false });
      } else {
        await createActivity({
          id: studentId,
          activity: data,
        });
        dispatch(addToast({ text: 'Activity created successfully', type: 'positive' }));
        methods.reset(defaultValues(), {
          keepDirty: false,
          keepErrors: false,
          keepTouched: false,
        });
      }

      onOpenChange(false);
    } catch (error) {
      dispatch(
        addToast({
          text: activity?.id ? 'Failed to update activity' : 'Failed to create activity',
          type: 'negative',
        }),
      );
      console.error('Failed to save activity:', error);
    }
  });

  const handleCancel = () => {
    methods.reset(activity, { keepDirty: false, keepErrors: false, keepTouched: false });
    onOpenChange(false);
  };

  return {
    methods,
    handleSave,
    handleCancel,
    isLoading: isCreating || isUpdating,
    isEdit: !!activity?.id,
  };
};
